/**
 * Notification Service
 * Handles API calls for notifications
 */
import api from './api';

const NOTIFICATIONS_BASE = '/api/user/notifications';

/**
 * Get all notifications with pagination
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Number of items per page
 * @param {boolean} unreadOnly - Filter to unread only
 * @returns {Promise<Object>} Paginated notifications
 */
export const getNotifications = async (
  page = 0,
  size = 20,
  unreadOnly = false
) => {
  const response = await api.get(NOTIFICATIONS_BASE, {
    params: { page, size, unreadOnly },
  });
  return response.data;
};

/**
 * Get unread notification count
 * @returns {Promise<number>} Number of unread notifications
 */
export const getUnreadCount = async () => {
  const response = await api.get(`${NOTIFICATIONS_BASE}/unread/count`);
  return response.data.count;
};

/**
 * Get a specific notification
 * @param {string} id - Notification ID
 * @returns {Promise<Object>} Notification object
 */
export const getNotification = async (id) => {
  const response = await api.get(`${NOTIFICATIONS_BASE}/${id}`);
  return response.data;
};

/**
 * Mark a notification as read
 * @param {string} id - Notification ID
 * @returns {Promise<void>}
 */
export const markAsRead = async (id) => {
  await api.patch(`${NOTIFICATIONS_BASE}/${id}/read`);
};

/**
 * Mark all notifications as read
 * @returns {Promise<Object>} Object with markedCount
 */
export const markAllAsRead = async () => {
  const response = await api.patch(`${NOTIFICATIONS_BASE}/read-all`);
  return response.data;
};

/**
 * Delete a notification
 * @param {string} id - Notification ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (id) => {
  await api.delete(`${NOTIFICATIONS_BASE}/${id}`);
};

/**
 * Notification type icons and colors mapping
 */
export const NOTIFICATION_TYPES = {
  payment_confirmed: {
    icon: 'Receipt',
    color: 'text-emerald-700 dark:text-emerald-300',
    bgColor: 'bg-emerald-100 dark:bg-emerald-500/15',
  },
  payment_failed: {
    icon: 'XCircle',
    color: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-500/15',
  },
  payment_disputed: {
    icon: 'AlertTriangle',
    color: 'text-orange-700 dark:text-orange-300',
    bgColor: 'bg-orange-100 dark:bg-orange-500/15',
  },
  document_uploaded: {
    icon: 'FileText',
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-100 dark:bg-blue-500/15',
  },
  documents_ready: {
    icon: 'FileCheck',
    color: 'text-emerald-700 dark:text-emerald-300',
    bgColor: 'bg-emerald-100 dark:bg-emerald-500/15',
  },
  filing_submitted: {
    icon: 'Clock',
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-100 dark:bg-blue-500/15',
  },
  filing_completed: {
    icon: 'CheckCircle',
    color: 'text-emerald-700 dark:text-emerald-300',
    bgColor: 'bg-emerald-100 dark:bg-emerald-500/15',
  },
  filing_rejected: {
    icon: 'XCircle',
    color: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-500/15',
  },
  compliance_reminder: {
    icon: 'AlertCircle',
    color: 'text-amber-700 dark:text-amber-300',
    bgColor: 'bg-amber-100 dark:bg-amber-500/15',
  },
  security_alert: {
    icon: 'Shield',
    color: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-500/15',
  },
  system_alert: {
    icon: 'Info',
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-100 dark:bg-blue-500/15',
  },
  ticket_created: {
    icon: 'MessageSquare',
    color: 'text-violet-700 dark:text-violet-300',
    bgColor: 'bg-violet-100 dark:bg-violet-500/15',
  },
  ticket_updated: {
    icon: 'MessageCircle',
    color: 'text-violet-700 dark:text-violet-300',
    bgColor: 'bg-violet-100 dark:bg-violet-500/15',
  },
  default: {
    icon: 'Bell',
    color: 'text-neutral-700 dark:text-neutral-200',
    bgColor: 'bg-neutral-100 dark:bg-neutral-700/70',
  },
};

/**
 * Get notification type configuration
 * @param {string} type - Notification type
 * @returns {Object} Icon and color configuration
 */
export const getNotificationTypeConfig = (type) => {
  return NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.default;
};

export default {
  getNotifications,
  getUnreadCount,
  getNotification,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  getNotificationTypeConfig,
  NOTIFICATION_TYPES,
};
